import React from 'react';
import GuidaPagina, { Sezione } from '../../components/GuidaPagina';
import { Tip, Step, H2, H3, P, B, Badge, Card, Row } from '../Guida';

// Guida per i collaboratori/allenatori (portale /allenatore o "modalità allenatore" per lo staff).
const SEZIONI = [
  { id: 'menu', label: 'Il tuo menu' },
  { id: 'presenze', label: 'Le mie presenze' },
  { id: 'segna', label: 'Segnare un allenamento' },
  { id: 'autocert', label: 'Autocertificazione' },
  { id: 'contratti', label: 'Contratti' },
  { id: 'documenti', label: 'Documenti' },
  { id: 'rimborsi', label: 'Rimborsi spese' },
  { id: 'altro', label: 'Dati, calendario, compiti' },
  { id: 'fipav', label: 'FIPAV — tornei' },
];

export default function GuidaAllenatore() {
  return (
    <GuidaPagina titolo="GUIDA ALLENATORI" sezioni={SEZIONI}
      sottotitolo="Come segnare e confermare le ore, chiedere un rimborso, firmare il contratto e caricare i documenti.">

      <Sezione id="menu">
        <H2>Il tuo menu</H2>
        <Card>
          <Row label="LE MIE PRESENZE"       desc="Le ore del mese: le confermi, le segnali o indichi un sostituto. Da qui segni anche un allenamento nuovo." />
          <Row label="I MIEI CONTRATTI"      desc="Il contratto da leggere e accettare. Il pallino rosso indica che ce n'è uno da confermare." />
          <Row label="I MIEI DOCUMENTI"      desc="Carta d'identità, certificato medico, qualifiche… e il CUD che carica la segreteria." />
          <Row label="I MIEI DATI"           desc="La tua anagrafica, IBAN e inquadramento: controllali e segnala se qualcosa non va." />
          <Row label="LA MIA TESSERA"        desc="Le tue tessere ASI e FIPAV con numero, stagione e scadenza." />
          <Row label="CALENDARIO ALLENATORI" desc="Chi allena quando, in tutte le sedi. Nessun compenso visibile." />
          <Row label="I MIEI RIMBORSI"       desc="Richieste di rimborso spese con lo scontrino o la ricevuta." />
        </Card>
        <Tip type="info">Se fai parte anche dello staff, queste voci le trovi premendo <B>PASSA A MODALITÀ ALLENATORE</B> in cima al menu (e <B>TORNA A MODALITÀ STAFF</B> per tornare indietro).</Tip>
        <Tip type="info">Da telefono il menu si apre con il pulsante <B>☰</B> in alto. Se una voce non c'è, la società l'ha spenta per ora.</Tip>
        <Tip type="ok">Password dimenticata? Nella pagina di accesso premi <B>Password dimenticata?</B>: ti arriva un link valido un'ora.</Tip>
      </Sezione>

      <Sezione id="presenze">
        <H2>Le mie presenze</H2>
        <P>Ogni allenamento inserito dalla segreteria ti arriva come presenza <Badge color="#888">INSERITA</Badge>. Per ognuna hai tre pulsanti:</P>
        <Card>
          <Row label="CONFERMO"    color="#22C55E" desc="Orario e luogo sono giusti. La presenza diventa CONFERMATA e conta per il compenso." />
          <Row label="SEGNALA"     color="#EF4444" desc="Qualcosa non torna (orario sbagliato, non c'eri): scrivi cosa. La segreteria la corregge." />
          <Row label="SOSTITUISCI" color="#8B5CF6" desc="Ti ha sostituito un collega: scegli chi. Il compenso passa a lui, la tua resta nello storico come SOSTITUITA." />
        </Card>
        <H3>Gli stati</H3>
        <Card>
          <Row label="INSERITA"     color="#888"    desc="Da controllare e confermare." />
          <Row label="CONFERMATA"   color="#22C55E" desc="Ok, conta per il compenso. Non si modifica più." />
          <Row label="SEGNALATA"    color="#EF4444" desc="Hai segnalato un problema: aspetta la correzione della segreteria." />
          <Row label="DA APPROVARE" color="#8B5CF6" desc="L'hai segnata tu: la segreteria deve approvarla. Non conta ancora." />
          <Row label="RIFIUTATA"    color="#EF4444" desc="La segreteria non l'ha approvata: resta visibile ma non conta." />
          <Row label="SOSTITUITA"   color="#A78BFA" desc="Al tuo posto è andato un collega." />
        </Card>
        <Tip type="warn">Per confermare le presenze di un mese serve prima l'<B>autocertificazione</B> (vedi sotto): finché non la fai, il pulsante Confermo resta spento.</Tip>
      </Sezione>

      <Sezione id="segna">
        <H2>Segnare un allenamento</H2>
        <P>Hai fatto un allenamento che non vedi in elenco? Segnalo tu.</P>
        <Step n="1">In <B>LE MIE PRESENZE</B> premi <B>SEGNA ALLENAMENTO</B>.</Step>
        <Step n="2">Scegli <B>ORARIO</B> (data, ora di inizio e fine, a quarti d'ora, luogo, note) oppure <B>ATTIVITÀ / EVENTI</B> (una voce del listino, per esempio un torneo).</Step>
        <Step n="3">Invia: la presenza compare come <Badge color="#8B5CF6">DA APPROVARE</Badge>.</Step>
        <Tip type="info">Finché la segreteria non la approva <B>non conta</B> nel compenso. Può approvarla correggendo l'orario o rifiutarla. Se hai sbagliato, puoi ritirarla tu finché è ancora da approvare.</Tip>
      </Sezione>

      <Sezione id="autocert">
        <H2>Autocertificazione mensile dei compensi</H2>
        <P>La legge sullo sport (D.Lgs. 36/2021) chiede di dichiarare, prima di ogni pagamento, quanto hai già ricevuto nell'anno come compensi sportivi, anche da <B>altre associazioni</B>.</P>
        <Step n="1">Quando ci sono presenze da confermare compare il riquadro <B>AUTOCERTIFICAZIONE COMPENSI RICHIESTA</B>.</Step>
        <Step n="2">Scrivi quanto hai ricevuto da <B>altre</B> associazioni fino alla fine del mese precedente (0 se nulla). Quanto ti abbiamo pagato noi lo calcola il gestionale.</Step>
        <Step n="3">Se sei dipendente pubblico, spunta la conferma dell'autorizzazione della tua amministrazione.</Step>
        <Step n="4">Premi <B>DICHIARO E CONFERMO</B>. Da lì puoi confermare le presenze del mese.</Step>
        <Tip type="ok">Le dichiarazioni fatte restano in <B>LE MIE AUTOCERTIFICAZIONI</B>, ognuna scaricabile in PDF.</Tip>
      </Sezione>

      <Sezione id="contratti">
        <H2>Contratti</H2>
        <Step n="1">Apri <B>I MIEI CONTRATTI</B> e premi <B>ANTEPRIMA</B> per leggerlo.</Step>
        <Step n="2">Se va bene, premi <B>CONFERMO E ACCETTO</B>.</Step>
        <Tip type="warn">La conferma online vale come accettazione, ma va comunque consegnata alla segreteria una <B>copia firmata a mano</B>.</Tip>
      </Sezione>

      <Sezione id="documenti">
        <H2>Documenti</H2>
        <Step n="1">In <B>I MIEI DOCUMENTI</B>, sezione <B>CARICA UN DOCUMENTO</B>, scegli il tipo (identità, codice fiscale, certificato medico, qualifica, altro).</Step>
        <Step n="2">Scegli il file: PDF, JPG o PNG, massimo 10 MB. Premi <B>CARICA</B>.</Step>
        <Tip type="info">Con <B>VEDI</B> lo apri a video. Puoi eliminare solo i documenti caricati da te; il CUD e quelli caricati dalla segreteria restano.</Tip>
      </Sezione>

      <Sezione id="rimborsi">
        <H2>Rimborsi spese</H2>
        <Step n="1">In <B>I MIEI RIMBORSI</B> premi <B>NUOVO RIMBORSO</B>: tipo di spesa (trasporto, materiale, vitto…), data della spesa, mese di competenza, importo e dettaglio.</Step>
        <Step n="2">Allega lo scontrino o la ricevuta: <B>obbligatorio</B>, un file PDF, JPG o PNG fino a 8 MB.</Step>
        <Step n="3">Premi <B>INVIA RICHIESTA</B>: resta in attesa finché la segreteria non la approva o la rifiuta. Finché è in attesa puoi ritirarla.</Step>
        <Tip type="warn">Massimo <B>300 € al mese</B> tra richieste approvate e in attesa. Se una richiesta viene rifiutata trovi il motivo: premi <B>CORREGGI</B> e poi <B>RINVIA RICHIESTA</B>.</Tip>
      </Sezione>

      <Sezione id="altro">
        <H2>Dati, calendario e compiti</H2>
        <H3>I miei dati</H3>
        <P>Anagrafica, IBAN, inquadramento fiscale e compensi da altre associazioni, in sola lettura. Se qualcosa è sbagliato <B>avvisa la segreteria</B>: lo corregge lei.</P>
        <H3>Calendario allenatori</H3>
        <P>Il calendario del mese con chi allena, quando e dove. Utile per organizzarsi e trovare un sostituto.</P>
        <H3>Compiti</H3>
        <P>Se la segreteria ti assegna un compito lo trovi in <B>LE MIE PRESENZE</B>: quando l'hai fatto, spuntalo.</P>
        <H3>Ordini, campi e supporto</H3>
        <P>Se il tuo account è da allenatore hai anche catalogo, ordini, affitto campo e supporto (quando sono accesi): come funzionano lo trovi nella <B>Guida utenti</B>. Per segnalare un dato sbagliato o chiedere qualcosa alla segreteria usa <B>SUPPORTO</B>: la risposta arriva lì, con un pallino rosso.</P>
      </Sezione>
      <Sezione id="fipav">
        <H2>FIPAV — tornei beach</H2>
        <P>Come per gli utenti: nella voce <B>FIPAV</B> vedi i tornei del calendario federale: per il Campionato Italiano per società chiedi alla società di partecipare, per gli altri tornei ti iscrivi tu su FIPAV e qui segnali con chi giochi (anche un atleta di un'altra società). Quando qualcuno ti indica come <B>allenatore</B> della sua coppia, il tuo nome compare sulla richiesta. I dettagli sono nella guida utenti, sezione FIPAV.</P>
      </Sezione>
    </GuidaPagina>
  );
}
